import type {
  AgentInfo,
  AssistantMessage,
  ContentPart,
  Message,
  Model,
  ModelRef,
  Permission,
  Provider,
  Session,
  Tokens,
  ToolState,
} from "./opencodeTypes"

// The agent's v1 session API, as the browser chat speaks it.
//
// The chat used to go through the agent's v2 "/api" surface. That runner is
// unfinished upstream: its tool list is built without MCP servers, so a
// workspace session declared the board tools, cached all seventeen, and handed
// the model none of them. The v1 surface is the one the CLI, the TUI and the
// desktop app use, and it is complete — board tools, real cost, compaction,
// permissions — so the chat now speaks it too.
//
// This file is the seam. Below: the wire shapes the agent sends, typed as far
// as the chat reads them, and the mapping onto the types the components
// render. Everything above it — MessageList, the dialogs, the status bar — is
// untouched; only what fills those types changed.
//
// Wire schemas, for when something here needs checking against the source:
//   agent/packages/schema/src/v1/session.ts        messages, parts, events
//   agent/packages/opencode/src/session/session.ts  Session.Info, CreateInput
//   agent/packages/opencode/src/provider/provider.ts providers and models
//   agent/packages/opencode/src/agent/agent.ts      agents
//   agent/packages/schema/src/v1/permission.ts      permission requests

export interface WireModelRef {
  providerID: string
  modelID: string
}

export interface WireTokens {
  input: number
  output: number
  reasoning: number
  total?: number
  cache?: { read: number; write: number }
}

export interface WireSession {
  id: string
  parentID?: string
  projectID: string
  workspaceID?: string
  directory: string
  title: string
  agent?: string
  model?: WireModelRef
  cost?: number
  tokens?: WireTokens
  // Milliseconds since the epoch, where the components expect ISO strings.
  time: { created: number; updated: number }
}

export interface WireUserInfo {
  id: string
  sessionID: string
  role: "user"
  time: { created: number }
  agent?: string
  model?: WireModelRef
}

export interface WireAssistantInfo {
  id: string
  sessionID: string
  role: "assistant"
  parentID: string
  modelID: string
  providerID: string
  agent: string
  variant?: string
  finish?: string
  summary?: boolean
  cost: number
  tokens: WireTokens
  error?: unknown
  time: { created: number; completed?: number }
}

export type WireMessageInfo = WireUserInfo | WireAssistantInfo

export type WireToolState =
  | { status: "pending"; input: Record<string, unknown>; raw?: string }
  | {
      status: "running"
      input: Record<string, unknown>
      title?: string
      metadata?: Record<string, unknown>
      time: { start: number }
    }
  | {
      status: "completed"
      input: Record<string, unknown>
      output: string
      title: string
      metadata: Record<string, unknown>
      time: { start: number; end: number }
    }
  | {
      status: "error"
      input: Record<string, unknown>
      error: string
      metadata?: Record<string, unknown>
      time: { start: number; end: number }
    }

interface WirePartBase {
  id: string
  sessionID: string
  messageID: string
}

export type WirePart =
  | (WirePartBase & {
      type: "text"
      text: string
      synthetic?: boolean
      ignored?: boolean
      time?: { start: number; end?: number }
    })
  | (WirePartBase & {
      type: "reasoning"
      text: string
      time: { start: number; end?: number }
      metadata?: Record<string, unknown>
    })
  | (WirePartBase & { type: "tool"; callID: string; tool: string; state: WireToolState })
  | (WirePartBase & { type: "file"; mime: string; filename?: string; url: string })
  | (WirePartBase & { type: "step-start" })
  | (WirePartBase & { type: "step-finish"; reason: string; cost: number; tokens: WireTokens })
  | (WirePartBase & { type: "retry"; attempt: number; error: unknown; time: { created: number } })
  | (WirePartBase & { type: "compaction" | "snapshot" | "patch" | "agent" | "subtask" })

export interface WireWithParts {
  info: WireMessageInfo
  parts: WirePart[]
}

export interface WireModel {
  id: string
  providerID?: string
  name: string
  family?: string
  status?: string
  limit?: { context?: number; input?: number; output?: number }
  capabilities?: { input?: unknown; output?: unknown }
  variants?: Record<string, Record<string, unknown>>
  api?: unknown
}

export interface WireProvider {
  id: string
  name: string
  source?: string
  models: Record<string, WireModel>
}

export interface WireProviderList {
  all: WireProvider[]
  default: Record<string, string>
  connected: string[]
}

export interface WireAgent {
  name: string
  description?: string
  mode: "subagent" | "primary" | "all"
  hidden?: boolean
}

export interface WirePermission {
  id: string
  sessionID: string
  permission: string
  patterns: string[]
  metadata?: Record<string, unknown>
  always?: string[]
  tool?: { messageID: string; callID: string }
}

export function toModelRef(ref: WireModelRef | undefined, variant?: string): ModelRef | undefined {
  if (!ref) return undefined
  return variant
    ? { providerID: ref.providerID, id: ref.modelID, variant }
    : { providerID: ref.providerID, id: ref.modelID }
}

export function fromModelRef(ref: ModelRef): WireModelRef {
  return { providerID: ref.providerID, modelID: ref.id }
}

function toTokens(tokens: WireTokens | undefined): Tokens | undefined {
  if (!tokens) return undefined
  return {
    input: tokens.input ?? 0,
    output: tokens.output ?? 0,
    reasoning: tokens.reasoning ?? 0,
    cache: { read: tokens.cache?.read ?? 0, write: tokens.cache?.write ?? 0 },
  }
}

// A running turn reports zeros until it settles. Those are not a measurement,
// and lib/sessionContext.ts skips them for the same reason; keeping whatever
// was known before is what stops the meter blinking to nothing mid-reply.
function measured(tokens: Tokens | undefined): Tokens | undefined {
  if (!tokens) return undefined
  const total = tokens.input + tokens.output + tokens.reasoning + tokens.cache.read + tokens.cache.write
  return total > 0 ? tokens : undefined
}

export function toSession(session: WireSession): Session {
  return {
    id: session.id,
    parentID: session.parentID,
    projectID: session.projectID,
    agent: session.agent,
    model: toModelRef(session.model),
    cost: session.cost ?? 0,
    tokens: toTokens(session.tokens) ?? { input: 0, output: 0, reasoning: 0, cache: { read: 0, write: 0 } },
    time: {
      created: new Date(session.time.created).toISOString(),
      updated: new Date(session.time.updated).toISOString(),
    },
    title: session.title,
    location: { directory: session.directory, workspaceID: session.workspaceID },
  }
}

function modalities(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : []
}

// One flat list across providers, which is what the composer's picker wants.
// A variant on the wire is a set of request overrides keyed by name; the
// picker only needs the names, and the wording for each lives in
// lib/slashCommands.ts.
export function toModels(list: WireProviderList): Model[] {
  return list.all.flatMap((provider) =>
    Object.values(provider.models ?? {}).map(
      (model): Model => ({
        id: model.id,
        providerID: model.providerID ?? provider.id,
        family: model.family,
        name: model.name,
        api: (model.api ?? { kind: "" }) as Model["api"],
        capabilities: {
          input: modalities(model.capabilities?.input),
          output: modalities(model.capabilities?.output),
        },
        request: {},
        variants: Object.entries(model.variants ?? {}).map(([id, body]) => ({ id, body })),
        time: {},
        cost: [],
        status: model.status ?? "active",
        enabled: true,
        limit: {
          context: model.limit?.context ?? 0,
          input: model.limit?.input,
          output: model.limit?.output ?? 0,
        },
      }),
    ),
  )
}

export function toProviders(list: WireProviderList): Provider[] {
  return list.all.map((provider) => ({
    id: provider.id,
    name: provider.name,
    api: { kind: provider.source ?? "" },
    request: {},
  }))
}

// The agent's identifier field is `name`; the composer picks by `id`.
export function toAgent(agent: WireAgent): AgentInfo {
  return { id: agent.name, description: agent.description, mode: agent.mode, hidden: agent.hidden }
}

export function toPermission(request: WirePermission): Permission {
  return {
    id: request.id,
    sessionID: request.sessionID,
    action: request.permission,
    resources: request.patterns ?? [],
    save: request.always,
    metadata: request.metadata,
    source: request.tool ? { type: "tool", messageID: request.tool.messageID, callID: request.tool.callID } : undefined,
  }
}

// --- Messages ---
//
// A v1 message is an `info` record plus a list of parts that arrive one at a
// time: the info first (`message.updated`), then each part (`message.part.updated`),
// with streamed text riding on `message.part.delta`. History hands over the
// same two things already assembled. So there is one builder for the info,
// one for a part, and history is the first folded over the second.

export function messageFromInfo(info: WireMessageInfo, previous?: Message): Message {
  if (info.role === "user") {
    const prev = previous?.type === "user" ? previous : undefined
    return {
      type: "user",
      id: info.id,
      // The text is a part, not a field, and it lands after this event.
      text: prev?.text ?? "",
      files: prev?.files,
      time: { created: info.time.created },
    }
  }

  const prev = previous?.type === "assistant" ? previous : undefined
  return {
    type: "assistant",
    id: info.id,
    agent: info.agent,
    model: toModelRef({ providerID: info.providerID, modelID: info.modelID }, info.variant),
    content: prev?.content ?? [],
    finish: info.finish ?? prev?.finish,
    cost: info.cost,
    tokens: measured(toTokens(info.tokens)) ?? prev?.tokens,
    error: info.error ?? prev?.error,
    summary: info.summary ?? prev?.summary,
  }
}

// Tool output on v1 is one string plus a title, where the renderer reads a
// list of content blocks — it was written against the v2 shape and there is
// no reason to make it learn a second one. `metadata` is the tool's own
// structured result, which the renderer already knows how to summarise.
function toToolState(state: WireToolState): ToolState {
  switch (state.status) {
    case "pending":
      return { status: "pending", input: state.input }
    case "running":
      return {
        status: "running",
        input: state.input,
        content: state.title ? [{ type: "text", text: state.title }] : [],
      }
    case "completed":
      return {
        status: "completed",
        input: state.input,
        content: state.output ? [{ type: "text", text: state.output }] : [],
        structured: state.metadata,
        result: state.metadata,
      }
    case "error":
      return { status: "error", input: state.input, error: state.error }
  }
}

function toContentPart(part: WirePart): ContentPart | undefined {
  switch (part.type) {
    case "text":
      // An ignored part was withdrawn by the agent; it is not part of the reply.
      if (part.ignored) return undefined
      return { type: "text", id: part.id, text: part.text }
    case "reasoning":
      return {
        type: "reasoning",
        id: part.id,
        text: part.text,
        providerMetadata: part.metadata,
        time: { created: part.time.start },
        // The renderer stops the block pulsing once this is set. On v2 it was a
        // client-side flag from `reasoning.ended`; here the part itself says
        // when it ended, which also means reloaded history gets it right.
        complete: part.time.end !== undefined,
      }
    case "tool":
      return { type: "tool", id: part.id, name: part.tool, state: toToolState(part.state) }
    default:
      return undefined
  }
}

// Mirrors the server's own findLast: a part id is only unique within the
// message that owns it, and a replay may hand the same part over twice.
function upsertPart(content: ContentPart[], part: ContentPart): ContentPart[] {
  for (let index = content.length - 1; index >= 0; index--) {
    if (content[index]?.id === part.id) {
      const next = content.slice()
      next[index] = part
      return next
    }
  }
  return [...content, part]
}

export function applyPart(message: Message, part: WirePart): Message {
  if (message.type === "user") {
    // Synthetic text is context the agent attached on the user's behalf, not
    // something they typed; it does not belong in their bubble.
    if (part.type === "text" && !part.synthetic && !part.ignored) return { ...message, text: part.text }
    if (part.type === "file") {
      const files = message.files ?? []
      if (files.some((file) => file.uri === part.url)) return message
      return { ...message, files: [...files, { name: part.filename, mime: part.mime, uri: part.url }] }
    }
    return message
  }

  if (message.type !== "assistant") return message

  // The per-step usage. Point-in-time, like the message-level figure, and it
  // arrives before the message's own tokens are filled in, so the meter can
  // move while a turn is still running.
  if (part.type === "step-finish") {
    return { ...message, cost: part.cost, tokens: measured(toTokens(part.tokens)) ?? message.tokens }
  }

  const mapped = toContentPart(part)
  if (!mapped) return message
  return { ...message, content: upsertPart(message.content, mapped) }
}

// Streamed text. `field` is the part field being appended to — "text" for
// both text and reasoning parts, which are the only ones that stream.
export function applyDelta(message: Message, partID: string, field: string, delta: string): Message {
  if (message.type !== "assistant" || field !== "text") return message
  for (let index = message.content.length - 1; index >= 0; index--) {
    const part = message.content[index]
    if (!part || part.id !== partID) continue
    if (part.type !== "text" && part.type !== "reasoning") return message
    const content = message.content.slice()
    content[index] = { ...part, text: part.text + delta }
    return { ...message, content }
  }
  return message
}

export function messageFromWithParts(message: WireWithParts): Message {
  return message.parts.reduce<Message>((draft, part) => applyPart(draft, part), messageFromInfo(message.info))
}

// The last real reply — never the summary a compaction writes. That message
// is authored by an internal, hidden agent ("compaction" on the wire, never
// one a person picks), so treating it as the last reply would switch the
// composer onto that agent, and the next prompt would actually run there.
export function lastAssistant(messages: Message[]): AssistantMessage | undefined {
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index]
    if (message?.type === "assistant" && !message.summary) return message
  }
  return undefined
}
